import axios from 'axios';
import { Globals } from '../../modules/Globals';
import { IRestaurantSettings } from './restaurants.service';
import {
  IBulkAvailabilityChange,
  IBulkAvailabilityResponse,
  IBulkDeleteResponse,
  IBulkMoveChange,
  IBulkMoveResponse,
  IBulkPriceChange,
  IBulkPricesResponse,
  ICategoryCreate,
  ICategoryDeleteResponse,
  ICategoryPatch,
  ICategoryResponse,
  ICatalogCreatedRefs,
  IItemCreate,
  IItemPatch,
  IItemResponse,
  IItemWriteResponse,
  IRestaurantSettingsPatch,
  TCategoryDeleteItems,
} from '../../interfaces/ICatalogV2';

const itemsUrl = Globals.apiPath + '/v2/items';
const categoriesUrl = Globals.apiPath + '/v2/categories';
const restaurantsUrl = Globals.apiPath + '/restaurants';

/** An API rolled back to a build without `created` answers without it: nothing is known as created. */
type TItemWriteAnswer = IItemResponse & { created?: ICatalogCreatedRefs };

function writeResponse(answer: TItemWriteAnswer): IItemWriteResponse {
  return {
    item: answer.item,
    created: answer.created ?? { sizes: [], groups: [] },
  };
}

/** Catalog writes of the Menú page (plan §3.5): partial, transactional, with undo data. */
export default class CatalogV2Service {
  static async createItem(body: IItemCreate): Promise<IItemWriteResponse> {
    const res = await axios.post<TItemWriteAnswer>(itemsUrl, body);
    return writeResponse(res.data);
  }

  static async patchItem(
    id: number,
    body: IItemPatch
  ): Promise<IItemWriteResponse> {
    const res = await axios.patch<TItemWriteAnswer>(`${itemsUrl}/${id}`, body);
    return writeResponse(res.data);
  }

  static async duplicateItem(
    id: number,
    body: { name: string }
  ): Promise<IItemResponse> {
    const res = await axios.post<IItemResponse>(
      `${itemsUrl}/${id}/duplicate`,
      body
    );
    return res.data;
  }

  static async bulkAvailability(
    restaurantId: number,
    changes: IBulkAvailabilityChange[]
  ): Promise<IBulkAvailabilityResponse> {
    const res = await axios.post<IBulkAvailabilityResponse>(
      `${itemsUrl}/bulk/availability`,
      { restaurantId, changes }
    );
    return res.data;
  }

  static async bulkPrices(
    restaurantId: number,
    changes: IBulkPriceChange[]
  ): Promise<IBulkPricesResponse> {
    const res = await axios.post<IBulkPricesResponse>(
      `${itemsUrl}/bulk/prices`,
      { restaurantId, changes }
    );
    return res.data;
  }

  static async bulkMove(
    restaurantId: number,
    changes: IBulkMoveChange[]
  ): Promise<IBulkMoveResponse> {
    const res = await axios.post<IBulkMoveResponse>(`${itemsUrl}/bulk/move`, {
      restaurantId,
      changes,
    });
    return res.data;
  }

  static async bulkDelete(
    restaurantId: number,
    itemIds: number[]
  ): Promise<IBulkDeleteResponse> {
    const res = await axios.post<IBulkDeleteResponse>(
      `${itemsUrl}/bulk/delete`,
      { restaurantId, itemIds }
    );
    return res.data;
  }

  static async createCategory(
    body: ICategoryCreate
  ): Promise<ICategoryResponse> {
    const res = await axios.post<ICategoryResponse>(categoriesUrl, body);
    return res.data;
  }

  static async patchCategory(
    id: number,
    body: ICategoryPatch
  ): Promise<ICategoryResponse> {
    const res = await axios.patch<ICategoryResponse>(
      `${categoriesUrl}/${id}`,
      body
    );
    return res.data;
  }

  static async deleteCategory(
    id: number,
    items: TCategoryDeleteItems
  ): Promise<ICategoryDeleteResponse> {
    const res = await axios.delete<ICategoryDeleteResponse>(
      `${categoriesUrl}/${id}`,
      { params: items }
    );
    return res.data;
  }

  static async patchSettings(
    restaurantId: number,
    body: IRestaurantSettingsPatch
  ): Promise<IRestaurantSettings> {
    const res = await axios.patch<IRestaurantSettings>(
      `${restaurantsUrl}/${restaurantId}/settings`,
      body
    );
    return res.data;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** The `code` of a catalog API error (e.g. `CATALOG_STALE`); null otherwise. */
export function catalogErrorCode(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  const data: unknown = error.response?.data;
  return isRecord(data) && typeof data.code === 'string' ? data.code : null;
}
